<template>
  <div class="new-campaign-wrapper">
    <div class="title">{{ $t('app.campaign.newPage.createTitle') }}</div>
    <div class="subtitle">
      <p>{{ $t('app.campaign.newPage.subtitle') }}</p>
    </div>
    <transition :name="state.currentTransition" mode="out-in">
      <div class="body" :key="state.currentIndex">
        <div :class="{'hidden': !canGoBack || state.yuzuAiIsWorking}" class="previous" @click="!canGoBack || state.yuzuAiIsWorking ? null : prev()">
          <i class="bi bi-arrow-up"></i>
          <span>{{ $t('app.campaign.newPage.buttons.previous') }}</span>
        </div>
        <div class="step-container" style="width: 100%;">
          <ChooseSpace v-if="currentStep === Steps.CHOOSE_SPACE" v-model="newCampaign" @update:modelValue="next()" @valid="state.canGoNext = $event"></ChooseSpace>
          <EnterCampaignName v-else-if="currentStep === Steps.ENTER_CAMPAIGN_NAME" v-model="newCampaign" @valid="state.canGoNext = $event"></EnterCampaignName>
          <div v-else-if="currentStep === Steps.ENTER_JOB" style="width: 100%;">
            <transition name="fade" mode="out-in">
              <EnterJob v-if="!state.yuzuAiIsWorking" v-model="newCampaign" @valid="state.canGoNext = $event"></EnterJob>
              <LoadJob v-else-if="state.yuzuAiIsWorking"></LoadJob>
            </transition>
          </div>
          <ConfirmJob v-else-if="currentStep === Steps.CONFIRM_JOB" @update:modelValue="assignWeightingBasedOnJob()" v-model="newCampaign" :yuzuAiResult="state.yuzuAiResult" @valid="state.canGoNext = $event"></ConfirmJob>
          <Recap v-else v-model="newCampaign" @runYuzuAi="reLaunchYuzuAi()" @configPanelIsOpen="(value) => state.configPanelIsOpen = value"></Recap>
        </div>
        <div class="next" :class="{'disabled': !isCurrentStepValid() || state.yuzuAiIsWorking, hidden: currentStep === Steps.RECAP}" @click="isCurrentStepValid() ? next() : null">
          <i class="bi bi-arrow-down"></i>
          <span>{{ $t('app.campaign.newPage.buttons.next') }}</span>
        </div>
        <div>
          <div class="done" v-tooltip="tooltip" :class="{'disabled': !validCampaign && currentStep === Steps.RECAP, 'hidden': currentStep !== Steps.RECAP}" @click="createCampaign()">
            <i class="bi bi-check2"></i>
            <span>{{ $t('app.campaign.newPage.buttons.validate') }}</span>
          </div>
          <div style="padding-top: 12px;" v-if="currentStep === Steps.RECAP">
            <span>{{ $t('app.campaign.newPage.didYouKnow') }}&nbsp;</span><div :style="{display: 'inline-block'}" class="yuzu-icon yuzu-ai"></div><span>&nbsp;{{ $t('app.campaign.newPage.aiInfo') }}</span>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.fade-enter-active {
  transition: opacity 0.6s ease;
}
.fade-leave-active {
  transition: opacity 0.4s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ----------------------------- */
/* Slide UP (previous)        */
/* ----------------------------- */
:deep(.slide-prev-enter-from) {
  transform: translateY(50%);
  opacity: 0;
}
:deep(.slide-prev-enter-to) {
  transform: translateY(0);
  opacity: 1;
}
:deep(.slide-prev-leave-from) {
  transform: translateY(0);
  opacity: 1;
}
:deep(.slide-prev-leave-to) {
  transform: translateY(-50%);
  opacity: 0;
}
:deep(.slide-prev-enter-active) {
  transition: all 0.5s ease;
}

:deep(.slide-prev-leave-active) {
  transition: all 0.3s ease;
}

/* ----------------------------- */
/* Slide DOWN (next)        */
/* ----------------------------- */
:deep(.slide-next-enter-from) {
  transform: translateY(-50%);
  opacity: 0;
}
:deep(.slide-next-enter-to) {
  transform: translateY(0);
  opacity: 1;
}
:deep(.slide-next-leave-from) {
  transform: translateY(0);
  opacity: 1;
}
:deep(.slide-next-leave-to) {
  transform: translateY(50%);
  opacity: 0;
}
:deep(.slide-next-enter-active){
  transition: all 0.5s ease;
}

:deep(.slide-next-leave-active) {
  transition: all 0.3s ease;
}

.new-campaign-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .title {
    color: var(--secondary-color);
    font-family: $bioSans;
    font-size: 32px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
  }
  
  .subtitle {
    color: var(--secondary-color);
    font-family: $bioSans;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }
}


.body {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  width: 70%;
  height: 100%;
  margin: 0 auto;
  margin-top: 12px;

  .step-container {
    width: 100%;

    padding: 32px;
    background-color: var(--white-color);

    border-radius: 12px;
    border: 1px solid var(--light-grey-color);
  }
}

.previous {
  display: flex;
  gap: 8px;
  align-items: center;

  position: relative;
  width: fit-content;
  padding: 12px 16px;
  margin: 0 auto;
  margin-bottom: 30px;

  color: var(--secondary-color);
  background-color: var(--white-color);
  border: 1px solid var(--light-grey-color);
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    color: $secondary-400-color;
  }

  &:active {
    background-color: $secondary-200-color;
    color: $secondary-color;
  }

  &.hidden {
    border-color: var(--light-white-color);
    color: var(--light-white-color);
    background-color: var(--light-white-color);
  }
}

.next {
  display: flex;
  gap: 8px;
  align-items: center;

  position: relative;
  width: fit-content;
  padding: 12px 16px;
  margin: 0 auto;
  margin-top: 30px;
  border-radius: 4px;

  background-color: $green-color;
  color: $white-color;
  cursor: pointer;

  &:hover {
    background-color: $green-400-color;
  }
  
  &:active {
    background-color: $green-600-color;
  }

  &.hidden {
    display: none;
  }

  &.disabled {
    background-color: var(--button-disabled-background);
    color: $secondary-color;
    cursor: not-allowed;

    &:hover {
      background-color: var(--button-disabled-background);
    }

    &:active {
      background-color: $secondary-200-color;
    }
  }
}

.done {
  display: flex;
  gap: 8px;
  align-items: center;

  position: relative;
  width: fit-content;
  padding: 12px 16px;
  margin: 0 auto;
  margin-top: 30px;

  background-color: $green-color;
  color: $white-color;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: $green-400-color;
  }
  
  &:active {
    background-color: $green-600-color;
  }

  &.hidden {
    display: none;
    border-color: var(--light-white-color);
    color: var(--light-white-color);
    background-color: var(--light-white-color);
  }

  &.disabled {
    background-color: $secondary-50-color;
    color: $secondary-color;
    cursor: not-allowed;

    &:hover {
      background-color: $white-600-color;
    }

    &:active {
      background-color: $secondary-200-color;
    }
  }
}
</style>

<script setup lang="ts">
import { reactive, ref, onMounted, onUnmounted } from "vue";
import { i18n } from "@/i18n/";
import { cloneDeep } from "lodash";
import { CampaignCreation, moduleLabels, WeightingModule, weightingModules } from "@/types/campaign";

import ChooseSpace from "./partials/steps/ChooseSpace.vue";
import EnterCampaignName from "./partials/steps/EnterCampaignName.vue";
import EnterJob from "./partials/steps/EnterJob.vue";
import LoadJob from "./partials/steps/LoadJob.vue";
import ConfirmJob from "./partials/steps/ConfirmJob.vue";
import Recap from "./partials/steps/Recap.vue";
import { useToast } from "vue-toastification";
import { useCreateCampaign, useEditCampaign, useTogglePublicInvitation } from "@/queries/campaigns.query";
import { api } from "@/api/client";

export interface AISkill {
  name: string;
}

export interface AISkillJob {
  job_title: string;
  skills: {
    required: AISkill[]; // mandatory
    mandatory: AISkill[]; // empty
    primary: AISkill[]; // important
    important: AISkill[]; // empty
    secondary: AISkill[];
    ignored: AISkill[];
  };
}

export interface AISkillsResult {
  query: string;
  results: AISkillJob[];
}

enum Steps {
  CHOOSE_SPACE = 0,
  ENTER_CAMPAIGN_NAME = 1,
  ENTER_JOB = 2,
  CONFIRM_JOB = 3,
  RECAP = 4,
}

// state.currentIndex is a position in this list, not a Steps value
const steps: Steps[] = [Steps.CHOOSE_SPACE, Steps.ENTER_CAMPAIGN_NAME, Steps.ENTER_JOB, Steps.CONFIRM_JOB, Steps.RECAP];

const route = useRoute();
const router = useRouter();
const toast = useToast();
const { locale, t } = useI18n();
const skillsStore = useSkillsStore();

const { mutate: postCampaign } = useCreateCampaign();
const { mutate: editCampaign } = useEditCampaign();
const { mutate: togglePublicInvitation } = useTogglePublicInvitation();

const state = reactive({
  currentIndex: 0,
  previousIndex: 0,
  currentTransition: "slide-next",
  canGoNext: false,
  yuzuAiIsWorking: false,
  yuzuAiResult: undefined as AISkillsResult | undefined,
  configPanelIsOpen: false,
});

const currentStep = computed(() => steps[state.currentIndex]);

const newCampaign: Ref<CampaignCreation> = ref({
  schema_version: "1.0.0",
  campaign_version: "3.0.0",
  organization_id: route.params.companyId as string,
  campaign: {
		parent_workspace_id: "",
		internal_name: "",
		headline: "",
		tagline: "",
		job_title: "",
		locale: locale.value === 'fr' ? 'fr-FR' : 'en-US',
		force_locale: true,
	},
	branding: {
		banner_image_id: null,
		background_image_id: null,
		text_color: "#000000",
		highlight_color: "#f2cb36",
	},
	assessment: {
		environment: "construction_site",
		modules: [],
    weighting: {
      required: [],
      primary: [],
      secondary: [],
      ignored: [],
    },
	},
	surveys: {
		satisfaction: true,
		demographic: true,
	},
	documents: {
		certification: {
			send_to_talent: false,
		},
		report: {
			send_to_talent: true,
			format: "simplified",
		}
	},
});

const canGoBack = computed(() => {
  return (state.currentIndex >= 1);
});

const haveAtLeastOneAssessedSkill = computed(() => {
  return newCampaign.value.assessment.modules.some((module) => {
    return !!skillsStore.skills.find((skill) => skill.id === module);
  })
});

const validCampaign = computed(() => {
  return (newCampaign.value.campaign.internal_name.trim() !== ""
    && newCampaign.value.campaign.headline.trim() !== ""
    && newCampaign.value.assessment.modules.length !== 0
    && haveAtLeastOneAssessedSkill.value
  );
});

const tooltip = computed(() => {
  if (newCampaign.value.campaign.internal_name.trim() === "") {
    return t('app.campaign.newPage.recap.errors.internalNameRequired');
  }
  if (newCampaign.value.campaign.headline.trim() === "") {
    return t('app.campaign.newPage.recap.errors.headlineRequired');
  }
  if (newCampaign.value.assessment.modules.length === 0 || !haveAtLeastOneAssessedSkill.value) {
    return t('app.campaign.newPage.recap.errors.atLeastModules')
  }
  return undefined;
});

function ensureWeightingIsCorrect() {
  if (typeof newCampaign.value.assessment.weighting === 'string') {
    return;
  }

  const weighting = newCampaign.value.assessment.weighting;
  skillsStore.skills.filter(s => s.type !== "style").forEach((skill) => {
    if (skill.disabled || skill.incompatible_versions.includes(newCampaign.value.campaign_version)) {
      weighting.required = weighting.required.filter(m => m !== skill.id && m !== skill.id_v1);
      weighting.primary = weighting.primary.filter(m => m !== skill.id && m !== skill.id_v1);
      weighting.secondary = weighting.secondary.filter(m => m !== skill.id && m !== skill.id_v1);
      weighting.ignored = weighting.ignored.filter(m => m !== skill.id && m !== skill.id_v1);
      return;
    }

    const allWeightingModules = [
      ...weighting.required,
      ...weighting.primary,
      ...weighting.secondary,
      ...weighting.ignored
    ];

    if (!allWeightingModules.includes(skill.id as WeightingModule) && !allWeightingModules.includes(skill.id_v1 as WeightingModule)) {
      weighting.required.push(skill.id as WeightingModule);
    }
  });
}

function assignWeightingBasedOnJob() {
  const selectedJobWeighting = state?.yuzuAiResult?.results?.find(v => v.job_title === newCampaign.value.campaign.job_title);
  if (!selectedJobWeighting) {
    newCampaign.value.assessment.weighting = {
      required: [...weightingModules].filter(m => m !== 'self_efficacy' as WeightingModule),
      primary: [],
      secondary: [],
      ignored: [],
    }
    newCampaign.value.assessment.modules = moduleLabels.filter((m) => m !== 'self_efficacy' as WeightingModule);
  } else {
    const weighting = {
      required: selectedJobWeighting.skills.required.map(v => v.name).filter(name => name !== "leadership") as WeightingModule[],
      primary: selectedJobWeighting.skills.primary.map(v => v.name).filter(name => name !== "leadership") as WeightingModule[],
      secondary: selectedJobWeighting.skills.secondary.map(v => v.name).filter(name => name !== "leadership") as WeightingModule[],
      ignored: selectedJobWeighting.skills.ignored.map(v => v.name).filter(name => name !== "leadership") as WeightingModule[],
    }
    newCampaign.value.assessment.weighting = cloneDeep(weighting);
    ensureWeightingIsCorrect();
    newCampaign.value.assessment.modules = moduleLabels.filter((m) => !weighting.ignored.includes(m as WeightingModule) && m !== 'self_efficacy' as WeightingModule);
  }
}

async function loadJob() {
  try {
    if (!newCampaign.value.campaign.job_title || newCampaign.value.campaign.job_title.trim() === "") {
      state.yuzuAiIsWorking = false;
      newCampaign.value.campaign.job_title = "";
      state.yuzuAiResult = undefined;
      assignWeightingBasedOnJob();

      // proceed to next step
      state.currentTransition = "slide-prev";
      state.previousIndex = state.currentIndex;
      state.currentIndex++;
      state.canGoNext = false;
    } else {
      state.yuzuAiIsWorking = true;
      state.yuzuAiResult = undefined;
      const result = await GetData(newCampaign.value.campaign.job_title);
      state.yuzuAiResult = result;
      newCampaign.value.campaign.job_title = state?.yuzuAiResult?.results?.[0]?.job_title || "";
      assignWeightingBasedOnJob();
    }
  } finally {
    state.yuzuAiIsWorking = false;
  }
}

async function next() {
  if (currentStep.value === Steps.ENTER_JOB) {
    await loadJob();
  }
  state.currentTransition = "slide-prev";
  state.previousIndex = state.currentIndex;
  state.currentIndex++;
  state.canGoNext = false;
}

function goToStep(step: Steps) {
  state.currentTransition = "slide-next";
  state.previousIndex = state.currentIndex;
  state.currentIndex = steps.indexOf(step);
}

function prev() {
  if (state.currentIndex === 0) return;
  if (currentStep.value === Steps.RECAP && (!newCampaign.value.campaign.job_title || newCampaign.value.campaign.job_title.trim() === "")) {
    goToStep(Steps.ENTER_JOB);
    return;
  }
  state.currentTransition = "slide-next";
  state.previousIndex = state.currentIndex;
  state.currentIndex--;
}

function isCurrentStepValid() {
  switch (currentStep.value) {
    case Steps.CHOOSE_SPACE:
      return (newCampaign.value.campaign.parent_workspace_id !== "");
    case Steps.ENTER_CAMPAIGN_NAME:
      return (newCampaign.value.campaign.internal_name.trim() !== "" && newCampaign.value.campaign.headline.trim() !== "");
    case Steps.ENTER_JOB:
      return true;
    case Steps.CONFIRM_JOB:
      return (newCampaign.value.campaign.job_title.trim() !== "");
    case Steps.RECAP:
      return validCampaign.value;
    default:
      return false;
  }
}

async function GetData(job: string): Promise<AISkillsResult> {
  const lang = i18n.global.locale.value;
  const response = await api.get(`/companies/${route.params.companyId}/skills?job=${job}&version=2&lang=${lang}`);
  let data: AISkillsResult = response.data;

  
  data.results.forEach((result) => {
    result.skills.required = result.skills.required.map(s => ({ name: skillsStore.getFormattedSkillId(s.name) }));
    result.skills.primary = result.skills.primary.map(s => ({ name: skillsStore.getFormattedSkillId(s.name) }));
    result.skills.secondary = result.skills.secondary.map(s => ({ name: skillsStore.getFormattedSkillId(s.name) }));
    result.skills.ignored = result.skills.ignored.map(s => ({ name: skillsStore.getFormattedSkillId(s.name) }));

    // Capitalize the first letter of each skill
    result.job_title = result.job_title.charAt(0).toUpperCase() + result.job_title.slice(1);
  });
  return data;
}

async function reLaunchYuzuAi() {
  // go back to enter job step
  goToStep(Steps.ENTER_JOB);

  // re-launch Yuzu AI
  await next();
}

function createCampaign() {
  if (!validCampaign.value) return;
  // remove style skills from weighting
  if (newCampaign.value.assessment.weighting !== 'neutral') {
    newCampaign.value.assessment.weighting.required = newCampaign.value.assessment.weighting.required.filter((skillId) => {
      const skill = skillsStore.skills.find(s => s.id === skillId || s.id_v2 === skillId || s.id_v1 === skillId);
      return skill && skill.type !== 'style';
    });
    newCampaign.value.assessment.weighting.primary = newCampaign.value.assessment.weighting.primary.filter((skillId) => {
      const skill = skillsStore.skills.find(s => s.id === skillId || s.id_v2 === skillId || s.id_v1 === skillId);
      return skill && skill.type !== 'style';
    });
    newCampaign.value.assessment.weighting.secondary = newCampaign.value.assessment.weighting.secondary.filter((skillId) => {
      const skill = skillsStore.skills.find(s => s.id === skillId || s.id_v2 === skillId || s.id_v1 === skillId);
      return skill && skill.type !== 'style';
    });
    newCampaign.value.assessment.weighting.ignored = newCampaign.value.assessment.weighting.ignored.filter((skillId) => {
      const skill = skillsStore.skills.find(s => s.id === skillId || s.id_v2 === skillId || s.id_v1 === skillId);
      return skill && skill.type !== 'style';
    });


    // add self_efficacy and remove from modules
    newCampaign.value.assessment.weighting.ignored.push('self_efficacy');
    newCampaign.value.assessment.modules = newCampaign.value.assessment.modules.filter((m) => m !== 'self_efficacy' as WeightingModule && m !== 'social_desirability' as WeightingModule);

    // add social_desirability if empathy, active_listening or social_desirability is present
    if (newCampaign.value.assessment.modules.includes('empathy' as WeightingModule) || newCampaign.value.assessment.modules.includes('empathy' as WeightingModule) || newCampaign.value.assessment.modules.includes('active_listening' as WeightingModule)) {
      newCampaign.value.assessment.modules.push('social_desirability' as WeightingModule);
    }
  }

  postCampaign({
    organisationId: route.params.companyId as string,
    newCampaign: newCampaign.value,
  }, {
    onSuccess: (data) => {
      togglePublicInvitation({
        context: { organisationId: route.params.companyId as string, spaceId: data.campaign.parent_workspace_id, campaignId: data.id },
        enabled: true,
      });
      editCampaign({ 
        context: {
          organisationId: route.params.companyId as string,
          spaceId: data.campaign.parent_workspace_id,
          campaignId: data.id,
        },
        campaign: { ...data, auto_activate: false },
      });
      toast.success(t('app.campaign.newPage.campaignCreated'));
      router.push({ name: 'campaign', params: { spaceId: newCampaign.value.campaign.parent_workspace_id, campaignId: data.id } });
    },
    onError: (error) => {
      toast.error(t('app.campaign.newPage.campaignCreationError'));
      console.error("Error creating campaign:", error);
    }
  });
}

function handleKeyPress(event: KeyboardEvent) {
  if (event.key === 'Enter' && state.canGoNext && !state.yuzuAiIsWorking && currentStep.value !== Steps.RECAP) {
    next();
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeyPress);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyPress);
});

</script>
